import EventDescription from "./EventDescription";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useHistory } from "react-router-dom";
import { useEffect, useState } from "react";

const DEFAULT_EVENT_IMAGE = "/images/default-event.jpg";

export default function EventCarousel({ activities = [] }) {
  const history = useHistory();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
  });

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: activities.length > 1,
      align: "center",
      skipSnaps: false,
    },
    activities.length > 1
      ? [
          Autoplay({
            delay: 8000,
            stopOnInteraction: false,
          }),
        ]
      : []
  );

  const handleMouseMove = (e) => {
    if (window.matchMedia("(hover: none)").matches) return;

    setTooltip({
      visible: true,
      x: e.clientX + 15,
      y: e.clientY - 15,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({
      visible: false,
      x: 0,
      y: 0,
    });
  };

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.emit("init");
  }, [activities.length, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const updateCarousel = () => {
      const slides = emblaApi.slideNodes();
      const total = slides.length;
      const selected = emblaApi.selectedScrollSnap();

      slides.forEach((slide) =>
        slide.classList.remove("is-active", "is-prev", "is-next")
      );

      if (total > 0) {
        const prevIndex = (selected - 1 + total) % total;
        const nextIndex = (selected + 1) % total;

        slides[selected]?.classList.add("is-active");
        slides[prevIndex]?.classList.add("is-prev");
        slides[nextIndex]?.classList.add("is-next");
      }

      setSelectedIndex(selected);
      setScrollSnaps(emblaApi.scrollSnapList());
    };

    emblaApi.on("select", updateCarousel);
    emblaApi.on("reInit", updateCarousel);
    emblaApi.on("init", updateCarousel);

    updateCarousel();

    return () => {
      emblaApi.off("select", updateCarousel);
      emblaApi.off("reInit", updateCarousel);
      emblaApi.off("init", updateCarousel);
    };
  }, [emblaApi]);

  return (
    <section
      className="event-carousel"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="embla__button embla__button--prev"
        onClick={() => emblaApi && emblaApi.scrollPrev()}
        aria-label="Precedent"
        type="button"
      >
        &lsaquo;
      </button>

      <button
        className="embla__button embla__button--next"
        onClick={() => emblaApi && emblaApi.scrollNext()}
        aria-label="Suivant"
        type="button"
      >
        &rsaquo;
      </button>

      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {activities.map((event) => (
            <div className="embla__slide" key={event._id || event.id}>
              <div
                className="event-slide"
                onClick={() =>
                  history.push(
                    event.incoming
                      ? event.isExternal
                        ? `/evenements/external?eventid=${event._id}`
                        : `/evenements/a-venir?eventid=${event._id}`
                      : event.isExternal
                      ? `/evenements/external?eventid=${event._id}`
                      : `/evenements/passes?eventid=${event._id}`
                  )
                }
              >
                <img src={event.image || DEFAULT_EVENT_IMAGE} alt={event.title} />
                <div className="event-overlay">
                  <span className="event-date">
                    {new Date(event.start_date).toLocaleDateString()}
                  </span>
                  <h2>{event.title}</h2>
                  <EventDescription value={event.description_general} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {scrollSnaps.length > 1 && (
        <div className="embla__dots" aria-label="Navigation du carousel">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`embla__dot ${index === selectedIndex ? "is-selected" : ""}`}
              type="button"
              onClick={() => emblaApi && emblaApi.scrollTo(index)}
              aria-label={`Aller a l'evenement ${index + 1}`}
            />
          ))}
        </div>
      )}

      {tooltip.visible && (
        <div
          className="event-tooltip"
          style={{
            top: tooltip.y,
            left: tooltip.x,
          }}
        >
          Voir plus de details
        </div>
      )}
    </section>
  );
}
